import { lazy } from "react";
import type { ComponentType, LazyExoticComponent, ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { SignUpLayout } from "@/components/layout/SignUpLayout";
import { ROUTES } from "@/constants/routes";

const LazyRoutes = {
    NotFound: lazy(() => import("@/pages/NotFound")),
    Dashboard: lazy(() => import("@/pages/Dashboard")),
    Login: lazy(() => import("@/pages/Login")),
    OnboardingPage: lazy(() => import("@/pages/OnboardingPage")),
    SignupOtpVerification: lazy(() => import("@/pages/SignupOtpVerification")),
    ForgotPassword: lazy(() => import("@/pages/ForgotPassword")),
    OnboardingKYCPage: lazy(() => import("@/pages/OnboardingKYCPage")),
    OnboardingSignatoryPage: lazy(() => import("@/pages/OnboardingSignatoryPage")),
    CreateProject: lazy(() => import("@/pages/CreateProject")),
    CreateProperty: lazy(() => import("@/pages/CreateProperty")),
    ProjectDetails: lazy(() => import("@/pages/ProjectDetails")),
    EscrowAccount: lazy(() => import("@/pages/EscrowAccount")),
    TeamAccessControl: lazy(() => import("@/pages/TeamAccessControl")),
    NeedToDevelop: lazy(() => import("@/pages/NeedToDevelop")),
    Subscription: lazy(() => import("@/pages/Subscription")),
    Payment: lazy(() => import("@/pages/Payment")),
    PropertyDetails: lazy(() => import("@/pages/PropertyDetails")),
    PaymentsDashboard: lazy(() => import("@/pages/PaymentsDashboard")),
    LeadManagement: lazy(() => import("@/pages/LeadManagement")),
    MediaVisualManager: lazy(() => import("@/pages/MediaVisualManager")),
    ProjectsDashboard: lazy(() => import("@/pages/Projects")),
    PropertiesDashboard: lazy(() => import("@/pages/Properties")),
    Analytics: lazy(() => import("@/pages/AnalyticsDashboard")),
    Inquiries: lazy(() => import("@/pages/BrokerInquiries")),
    Profile: lazy(() => import("@/pages/ProfileInfo")),
};

export type PageComponent = LazyExoticComponent<ComponentType<any>> | typeof Outlet;

export interface RouteDefinition {
    path: string;
    component: PageComponent;
    children?: RouteDefinition[];
}

export interface LayoutGroup {
    layout: ComponentType<{ children: ReactNode }>;
    isPrivate: boolean;
    routes: RouteDefinition[];
}

export const routesConfig: LayoutGroup[] = [
    {
        layout: AuthenticatedLayout,
        isPrivate: true,
        routes: [
            { path: ROUTES.MAIN.DASHBOARD, component: LazyRoutes.Dashboard },
            { path: ROUTES.MAIN.ONBOARDING_KYC, component: LazyRoutes.OnboardingKYCPage },
            { path: ROUTES.MAIN.ONBOARDING_SIGNATORY, component: LazyRoutes.OnboardingSignatoryPage },
            { path: ROUTES.MAIN.CREATEPROJECT, component: LazyRoutes.CreateProject },
            { path: ROUTES.MAIN.ESCROW, component: LazyRoutes.EscrowAccount },
            { path: ROUTES.MAIN.TEAM, component: LazyRoutes.TeamAccessControl },
            { path: ROUTES.MAIN.SUBSCRIPTION, component: LazyRoutes.Subscription },
            { path: ROUTES.MAIN.PAYMENT, component: LazyRoutes.Payment },
            { path: ROUTES.MAIN.PAYMENTS_DASHBOARD, component: LazyRoutes.PaymentsDashboard },
            { path: ROUTES.MAIN.PROJECT_DETAILS, component: LazyRoutes.ProjectDetails },
            { path: ROUTES.MAIN.CREATE_PROPERTY, component: LazyRoutes.CreateProperty },
            { path: ROUTES.MAIN.PROJECT_PROPERTY_DETAILS, component: LazyRoutes.PropertyDetails },
            //{ path: ROUTES.MAIN.PROPERTY_DETAILS, component: LazyRoutes.PropertyDetails },
            { path: ROUTES.BOTTOM.INFO, component: LazyRoutes.NeedToDevelop },
            { path: ROUTES.MAIN.ANALYTICS, component: LazyRoutes.Analytics },
            { path: ROUTES.MAIN.LEADS, component: LazyRoutes.LeadManagement },
            { path: ROUTES.MAIN.PROJECTS, component: LazyRoutes.ProjectsDashboard },
            { path: ROUTES.MAIN.PROPERTIES, component: LazyRoutes.PropertiesDashboard },
            { path: ROUTES.MAIN.INQUIRIES, component: LazyRoutes.Inquiries },
            { path: ROUTES.MAIN.MEDIA_AND_VISUALS, component: LazyRoutes.MediaVisualManager },
            { path: ROUTES.BOTTOM.SETTINGS, component: LazyRoutes.Profile },
            { path: "*", component: LazyRoutes.NotFound },
        ],
    },
    {
        layout: SignUpLayout,
        isPrivate: false,
        routes: [
            { path: ROUTES.AUTH.LOGIN, component: LazyRoutes.Login },
            { path: ROUTES.AUTH.SIGNUP, component: LazyRoutes.OnboardingPage },
            { path: ROUTES.AUTH.SIGNUP_OTP, component: LazyRoutes.SignupOtpVerification },
            { path: ROUTES.AUTH.FORGOT_PASSWORD, component: LazyRoutes.ForgotPassword },
            { path: "*", component: LazyRoutes.NotFound },
        ],
    },
];
